import React from 'react';
import { LeaveEntry, PublicHoliday } from '@techgitter/contracts';
import { WorkItem } from '../workitems/workItemTypes';
import { CalendarAppointment, CalendarAppointmentVM } from './CalendarAppointment';
import './CalendarDay.css';

/** A technician's leave that overlaps this day. */
export interface CalendarLeaveVM {
  leave: LeaveEntry;
  technicianName: string;
}

export interface CalendarDayViewModel {
  date: Date;
  dateStr: string;
  isToday: boolean;
  /** Pre-formatted header label, e.g. "Mon 18 May". */
  label: string;
}

interface CalendarDayProps {
  day: CalendarDayViewModel;
  appointments: CalendarAppointmentVM[];
  leave: CalendarLeaveVM[];
  holiday?: PublicHoliday | null;
  onAppointmentClick: (e: React.MouseEvent, workItem: WorkItem) => void;
  onLeaveClick: (leave: LeaveEntry) => void;
}

/**
 * One day in the V2 calendar — a header row (label, optional holiday pill,
 * appointment count) over an events area listing leave + appointment cards.
 * Reused for both the week list and the single-day view.
 */
export const CalendarDay: React.FC<CalendarDayProps> = ({
  day,
  appointments,
  leave,
  holiday,
  onAppointmentClick,
  onLeaveClick,
}) => {
  const hasContent = appointments.length > 0 || leave.length > 0;

  return (
    <div className={`cal-day ${day.isToday ? 'cal-day--today' : ''}`}>
      <div className="cal-day__header">
        <span className={`cal-day__label ${day.isToday ? 'cal-day__label--today' : ''}`}>
          {day.label}
        </span>
        {holiday && (
          <span className="cal-day__holiday" title={holiday.name}>
            {holiday.name}
          </span>
        )}
        {appointments.length > 0 && (
          <span className="cal-day__count">
            {appointments.length} appointment{appointments.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {hasContent ? (
        <div className="cal-day__events">
          {leave.map((l) => (
            <div
              key={l.leave.uuid}
              className="cal-leave"
              onClick={() => onLeaveClick(l.leave)}
            >
              <span className="cal-leave__label">On leave</span>
              <span className="cal-leave__type">{l.leave.leaveType}</span>
              <span className="cal-leave__tech">{l.technicianName}</span>
            </div>
          ))}
          {appointments.map((appt) => (
            <CalendarAppointment
              key={appt.workItem.guid}
              appointment={appt}
              onClick={onAppointmentClick}
            />
          ))}
        </div>
      ) : (
        <div className="cal-day__empty">No appointments</div>
      )}
    </div>
  );
};
